import {
  CustomAttributesTypes,
  CustomAttributeValue,
  FilterFacetsTypes
} from 'components/common/Portals/CustomAttributes/types';
import { SegmentTag } from './global';
import { PrivateAttributes, ProductType } from './Products';
import { Option } from 'types/global';

export type SearchTarget = ProductType | 'SUPPLIERS';
export interface SearchState {
  query: string;
  target: SearchTarget;
  segment: SegmentTag;
  from: number;
  size: number;
  //Filters is undefined at the start, because we need some time to have the whole data which is delayed by the custom attributes fetch, it becomes defined once we have the needed information
  filters?: SearchFilter[];
  sort: SearchSort[];
  eventKey?: string;
  source: SearchSource;
  hideLoadingIndicator: boolean;
  isFetchingMore: boolean;
}
export enum SearchSource {
  ALL = 'all',
  BOOKMARKS = 'bookmarks'
}

export interface SearchRequest {
  domain?: string;
  query: string;
  target: SearchTarget;
  segment: SegmentTag;
  from?: number;
  size: number;
  filters: SearchFilter[];
  search_after?: string[];
  search?: {
    target: ProductType;
  };
  sort?: SearchSort[];
  eventKey?: string;
}

export interface SearchFilter {
  field: string;
  value: string;
  label: string;
  context?: string;
}
export enum SearchSortField {
  NAME = 'name.keyword',
  SCORE = '_score',
  EVENT_RANK = 'event.rank',
  EVENT_DISPLAY_NAME = 'event.display_name.keyword',
  EVENT_CREATED_AT = 'event.created_at',
  RANDOM = 'randomxyz'
}
export enum SearchSortNestedField {
  EVENT = 'event'
}
export interface SearchSort {
  field: SearchSortField;
  direction: SortDirection;
  nested_field?: SearchSortNestedField;
}

export interface SearchFilters {
  field?: string;
  value?: string[];
}
export interface SearchResults {
  total: number;
  results: SearchResult[];
  facets: Facets;
  search?: { segment: SegmentTag; target: ProductType };
}

export interface SearchResult {
  document: SearchDocument;
  sort_values: string[];
}
export enum FacetsEnum {
  APPLICATIONS = 'APPLICATIONS',
  PHYSICAL_PROPERTIES = 'PHYSICAL PROPERTIES',
  APPEARANCES = 'APPEARANCES',
  CHEMICAL_PROPERTIES = 'CHEMICAL PROPERTIES',
  ORIGIN = 'ORIGIN',
  REGIONS = 'REGIONS',
  COMPANIES = 'COMPANIES',
  COUNTRIES = 'COUNTRIES',
  SUSTAINABILITY_CLAIMS = 'SUSTAINABILITY CLAIMS',
  CERTIFICATIONS = 'CERTIFICATIONS',
  FUNCTIONS = 'FUNCTIONS',
  COLOR = 'COLOR',
  CHEMICALGROUPS = 'CHEMICALGROUPS',
  PERFORMANCE_CLAIMS = 'PERFORMANCE CLAIMS',
  SEGMENTS = 'SEGMENTS',
  SPECIALIZATIONS = 'SPECIALIZATIONS',
  SERVICES = 'SERVICES',
  ODOR = 'ODOR',
  CATEGORY = 'CATEGORY'
}

export interface Facets {
  applications?: Facet[];
  physical_properties?: Facet[];
  appearances?: Facet[];
  chemical_properties?: Facet[];
  origin?: Facet[];
  regions?: Facet[];
  companies?: Facet[];
  countries?: Facet[];
  sustainability_claims?: Facet[];
  certifications?: Facet[];
  functions?: Facet[];
  color?: Facet[];
  chemicalgroups?: Facet[];
  performance_claims?: Facet[];
  segments?: Facet[];
  specializations?: Facet[];
  services?: Facet[];
  odor?: Facet[];
  category?: Facet[];
  verified_companies?: Facet[];
  status?: Facet[];
  projects?: Facet[];
  //ingredients?: Facet[];
  brandPortalCompanyLabel?: Facet[];
  ['ingredients_labels']?: Facet[];
}

export interface Facet {
  value: string;
  title?: string;
  count: number;
  //needed for showing labels
  color?: string;
}

export type FilterOption =
  | {
      label: string;
      key: string;
      type?:
        | CustomAttributesTypes.MULTI_SELECT
        | CustomAttributesTypes.SINGLE_SELECT
        | CustomAttributesTypes.TEXT;
    }
  | {
      label: string;
      key: string;
      type?: FilterFacetsTypes.BOOLEAN;
      value: boolean;
    };

export interface ActiveFilter {
  value: Option[];
  property: string;
}

// Food ingredients TODO:
// extend SearchDocument to accept key values related with food ingredients? not sure but i think this is the right interface to extend
export interface SearchDocument {
  awards?: any; //Todo add the correct type of awards []
  bookmarked?: boolean;
  marketing: Marketing;
  completion_rate: number;
  score: string;
  identifiers: Identifiers;
  segment: SegmentTag;
  name: string;
  old_key: string;
  company: SearchCompanyInfo;
  categories: Categories;
  id: string;
  type: ProductType;
  key: string;
  //TODO: This needs typing
  event: any;
  assets?: any;
  position?: number;
}

export interface SupplierSearchDocument {
  score: string;
  name: string;
  old_key: string;
  id: string;
  key: string;
  indie_brand_friendly?: boolean;
  verified?: boolean;
  exhibitions: Exhibitions[];
}

export interface Exhibitions {
  event: {
    name: string;
    key: string;
  };
  properties: {
    stand: {
      name: string;
    };
  };
}
export interface EventSearchDocument extends SearchDocument {
  event: {
    event: { name: string; key: string };
    product_private_attributes?: PrivateAttributes;
    key: string;
    product_custom_attributes?: {
      definition: { key: string };
      value: CustomAttributeValue;
    }[];
  };
}

export interface Marketing {
  description: string;
  webpage: string;
}

export interface Identifiers {
  cas: string[];
  iupac: string[];
  inci: string[];
}

export interface SearchCompanyInfo {
  headquarters: string;
  djsi: number;
  verified_contact: VerifiedContract;
  name: string;
  verified: boolean;
  sustainable_goals: string[];
  type: string;
  product_contact: ProductContact;
  key: string;
  number_employees: string;
  event?: any;
}

export interface VerifiedContract {
  name: string;
  type: string;
  value: string;
  primary: boolean;
}

export interface ProductContact {
  type: string;
  value: string;
  primary: boolean;
}

export interface Categories {
  technical: CategoryType;
  general: CategoryType;
  'regional-availability': CategoryType;
  'formulation-procedure': FormulationProcedureType;
  compliance: CategoryType;
}
export interface CategoryType {
  functions: CategoryDetail[];
  organoleptic: CategoryDetail[];
  claims: CategoryDetail[];
  certifications: CategoryDetail[];
  properties: CategoryDetail[];
  applications: CategoryDetail[];
  chemicalgroup: CategoryDetail[];
  origin: CategoryDetail[];
  usagelevel: CategoryDetail;
  'origin-species': CategoryDetail;
  rci: CategoryDetail;
  regions: CategoryDetail[];
  countries: CategoryDetail[];
  specialization: CategoryDetail[];
  samples: CategoryDetail[];
  hazardstatements: CategoryDetail[];
  hazardclassification: CategoryDetail[];
}

export interface FormulationProcedureType {
  formulation: {
    value: string;
    properties: {
      phase: string;
      order: number;
      weight: string;
    };
  }[];
  procedure: {
    properties: {
      order: number;
      value: string;
    };
  }[];
}
export interface CategoryDetail {
  value: string;
  properties?: Property;
}

export interface Property {
  type?: SearchCategoryType;
  primary?: boolean;
  phase?: string;
  supplier?: string;
  functions?: string | string[];
  inci?: string | string[];
  weight?: string;
}

export interface CompareOption {
  label: string;
  key: string;
}

export interface FilterForm {
  [key: string]: FilterFormItem;
}

export interface FilterFormItem {
  [key: string]: boolean;
}

export enum SortDirection {
  ASCENDENT = 'asc',
  DESCENDENT = 'desc',
  RANDOM = 'random'
}

export enum SearchCategoryType {
  BOTANICAL = 'BOTANICAL',
  SUSTAINABILITY = 'SUSTAINABILITY',
  PERFORMANCE = 'PERFORMANCE',
  COLOR = 'COLOR',
  ODOR = 'ODOR',
  PHYSICAL = 'PHYSICAL',
  CHEMICAL = 'CHEMICAL',
  APPEARANCES = 'APPEARANCES',
  CERTIFICATIONS = 'CERTIFICATIONS',
  FREE_FROM_CLAIMS = 'FREE_FROM_CLAIMS',
  FLAVOR_FAMILY = 'FLAVOR_FAMILY',
  FLAVOR = 'FLAVOR'
}

export type FacetOrder =
  | {
      label: 'Name';
      value: 'NAME';
    }
  | { label: 'Count'; value: 'COUNT' };
